import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { FormFieldDef } from '@hcc/types';
import { Paperclip, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, ApiError } from '../../lib/api';
import { readAsDataUrl, uploadFormFieldVideo, FORM_VIDEO_EXTS } from '../../lib/upload';
import { compressImage } from '../../lib/image';

// Keep clear of the 10 MB API/nginx body cap (base64 ~ byte count, + JSON).
const MAX_UPLOAD_BASE64 = 9_000_000;

/** A file already persisted to the draft on the server. */
export interface SavedFile {
  name: string;
  key: string;
}

interface FormFileUploadFieldProps {
  taskId: string;
  field: FormFieldDef;
  files: SavedFile[];
  /** Names from the last submission, shown as a keep/replace hint. */
  priorNames: string[];
  onUploaded: (file: SavedFile) => void;
  onDeleted: (key: string) => void;
  /**
   * Reports while a pick is uploading. The form uses it to hold Submit:
   * submitting mid-upload discards the draft's pending record server-side,
   * so the upload could never complete.
   */
  onBusyChange?: (busy: boolean) => void;
}

/**
 * File field that persists each pick to the server straight away (and deletes
 * from storage on removal), so uploads survive a refresh like the rest of the
 * autosaved form. Distinct from [[FileUploadField]], which holds files in the
 * browser until a one-shot submit (still used by the standalone upload task).
 */
export function FormFileUploadField({
  taskId,
  field,
  files,
  priorNames,
  onUploaded,
  onDeleted,
  onBusyChange,
}: FormFileUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const accept = (field.accept ?? []).map((e) => '.' + e).join(',');
  const [deleting, setDeleting] = useState<string | null>(null);
  // 0..1 while a video streams to storage, null otherwise.
  const [videoProgress, setVideoProgress] = useState<number | null>(null);

  const upload = useMutation({
    mutationFn: (input: { filename: string; base64: string }) =>
      api<SavedFile>(`/api/me/onboarding/forms/${taskId}/files`, {
        method: 'POST',
        body: JSON.stringify({ field_key: field.key, ...input }),
      }),
    onSuccess: (saved) => onUploaded(saved),
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Could not upload file'),
  });

  const remove = useMutation({
    mutationFn: (key: string) =>
      api<{ ok: true }>(`/api/me/onboarding/forms/${taskId}/files/${key}`, {
        method: 'DELETE',
      }),
    onMutate: (key) => setDeleting(key),
    onSuccess: (_res, key) => onDeleted(key),
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Could not remove file'),
    onSettled: () => setDeleting(null),
  });

  // Re-encode photos to JPEG before upload when the field accepts jpg, so a
  // multi-MB phone photo of a cert fits under the body cap instead of 413ing.
  const canJpeg = (field.accept ?? []).some((e) => e === 'jpg' || e === 'jpeg');

  async function prepare(f: File): Promise<{ filename: string; base64: string }> {
    if (f.type.startsWith('image/') && canJpeg) {
      const base64 = await compressImage(f, 2000, 0.85);
      return { filename: f.name.replace(/\.[^.]+$/, '') + '.jpg', base64 };
    }
    return { filename: f.name, base64: await readAsDataUrl(f) };
  }

  function isVideo(f: File): boolean {
    const ext = f.name.toLowerCase().split('.').pop() ?? '';
    return FORM_VIDEO_EXTS.includes(ext);
  }

  async function onPick(list: FileList | null) {
    if (!list || list.length === 0) return;
    // Single-value fields upload only the first pick; multiple append in order.
    const chosen = field.multiple ? Array.from(list) : [list[0]!];
    onBusyChange?.(true);
    try {
      for (const f of chosen) {
        if (isVideo(f)) {
          // Videos bypass the base64 body cap: the bytes stream straight to
          // storage (presigned PUT on S3) and register on the draft after.
          setVideoProgress(0);
          try {
            const saved = await uploadFormFieldVideo(taskId, field.key, f, setVideoProgress);
            onUploaded(saved);
          } catch (err) {
            toast.error(err instanceof ApiError ? err.message : `Could not upload "${f.name}"`);
          } finally {
            setVideoProgress(null);
          }
          continue;
        }
        const prepared = await prepare(f);
        if (prepared.base64.length > MAX_UPLOAD_BASE64) {
          toast.error(`"${f.name}" is too large to upload. Try a smaller file or photo.`);
          continue;
        }
        await upload.mutateAsync(prepared);
      }
    } catch {
      // The mutation's onError already toasts; swallow here so a failed
      // upload doesn't surface as an unhandled promise rejection.
    } finally {
      onBusyChange?.(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  const canAddMore = field.multiple || files.length === 0;

  return (
    <fieldset className="block text-sm">
      <span className="font-medium text-fg-muted">
        {field.label}
        {field.required ? <span className="text-danger"> *</span> : null}
      </span>
      <div className="mt-1.5 space-y-2">
        {files.map((f) => (
          <div
            key={f.key}
            className="flex items-center gap-2 rounded-token-sm border border-line/10 bg-surface-2 px-2.5 py-1.5 text-sm"
          >
            <Paperclip className="h-4 w-4 text-fg-dim" />
            <span className="min-w-0 flex-1 truncate text-fg">{f.name}</span>
            <button
              type="button"
              disabled={deleting === f.key}
              onClick={() => remove.mutate(f.key)}
              className="rounded-token-sm p-1 text-fg-dim hover:text-danger disabled:opacity-50"
              aria-label="Remove file"
            >
              {deleting === f.key ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <X className="h-4 w-4" />
              )}
            </button>
          </div>
        ))}
        {canAddMore ? (
          <input
            ref={inputRef}
            type="file"
            accept={accept || undefined}
            multiple={field.multiple}
            disabled={upload.isPending || videoProgress !== null}
            onChange={(e) => onPick(e.target.files)}
            className="block w-full text-sm text-fg-muted file:mr-3 file:rounded-token-sm file:border-0 file:bg-surface-2 file:px-3 file:py-1.5 file:text-sm file:text-fg-muted disabled:opacity-50"
          />
        ) : null}
        {videoProgress !== null ? (
          <div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-surface-2">
              <div
                className="h-full rounded-full bg-accent transition-all"
                style={{ width: `${Math.round(videoProgress * 100)}%` }}
              />
            </div>
            <p className="mt-1 text-xs text-fg-dim">
              Uploading video… {Math.round(videoProgress * 100)}%. Keep this page open.
            </p>
          </div>
        ) : upload.isPending ? (
          <p className="flex items-center gap-1.5 text-xs text-fg-dim">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading…
          </p>
        ) : null}
      </div>
      {priorNames.length > 0 && files.length === 0 ? (
        <span className="mt-1 block text-xs text-fg-dim">
          Already on file: {priorNames.join(', ')}. Leave empty to keep, or attach a new
          file to replace.
        </span>
      ) : (
        <span className="mt-1 block text-xs text-fg-dim">
          {field.help ? field.help + ' ' : ''}
          Allowed: {(field.accept ?? []).join(', ') || 'any safe file type'}
        </span>
      )}
    </fieldset>
  );
}
